import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useJourney, STAGE } from '../../context/JourneyContext';
import { useMockDate } from '../../context/MockDateContext';
import { Bell, Moon, Sun, LogOut, Settings, User, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { TinyStepsLogo } from '../ui/FloatingDecorations';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const pageTitles = {
  '/dashboard': null,
  '/period':     null, /* resolved dynamically below */
  '/period-log': null, /* resolved dynamically below */
  '/pregnancy': 'Pregnancy',
  '/baby':      'Baby Care',
  '/memories':  'Memory Vault',
  '/medical':   'Medical Records',
  '/delivery':  'Delivery Prep',
  '/community': 'Community',
  '/ai':        'AI Companion',
  '/profile':   'Profile & Settings',
};

const Navbar = () => {
  const { user, logout }       = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate               = useNavigate();
  const { pathname }           = useLocation();
  const [showDropdown, setDropdown] = useState(false);
  const [showNotif,    setNotif]    = useState(false);

  const { stage } = useJourney();
  const { mockDate, isMocked, setMockDate, clearMock } = useMockDate();

  /* ── Date picker state ── */
  const now = isMocked ? mockDate : new Date();
  const [showPicker,   setShowPicker]   = useState(false);
  const [viewYear,     setViewYear]     = useState(now.getFullYear());
  const [viewMonth,    setViewMonth]    = useState(now.getMonth());
  const pickerRef = useRef(null);

  /* Sync picker view when mock date changes */
  useEffect(() => {
    const d = isMocked ? mockDate : new Date();
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }, [isMocked, mockDate]);

  /* Close picker on outside click */
  useEffect(() => {
    if (!showPicker) return;
    const handler = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPicker]);

  /* Calendar grid for the current viewYear/viewMonth */
  const calGrid = (() => {
    const firstDow = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  })();

  const selectedDay   = isMocked ? mockDate.getDate()    : null;
  const selectedMonth = isMocked ? mockDate.getMonth()   : null;
  const selectedYear  = isMocked ? mockDate.getFullYear(): null;
  const realToday     = new Date();
  const isToday = (d) => !isMocked && d === realToday.getDate() && viewMonth === realToday.getMonth() && viewYear === realToday.getFullYear();
  const isSelected = (d) => isMocked && d === selectedDay && viewMonth === selectedMonth && viewYear === selectedYear;

  const title = (pathname === '/period' || pathname === '/period-log')
    ? 'Cycle Story'
    : pageTitles[pathname];
  const isDash    = pathname === '/dashboard';

  const notifications = [
    { id:1, text:'Time to drink water!',              time:'Just now' },
    { id:2, text:'Weekly pregnancy update ready',     time:'1 hr ago' },
    { id:3, text:"Don't forget your prenatal vitamins", time:'3 hr ago' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 lg:left-64 z-40 border-b border-blush-100/60 dark:border-gray-800 h-14"
      style={{ background:'rgba(253,240,252,0.85)', backdropFilter:'blur(20px) saturate(200%)', WebkitBackdropFilter:'blur(20px) saturate(200%)', boxShadow:'0 2px 12px rgba(249,114,182,0.08)' }}>
      <div className="flex items-center h-full px-4 relative">

        {/* ── Left ── */}
        {isDash ? (
          <Link to="/dashboard" className="flex items-center gap-2 lg:hidden">
            <TinyStepsLogo size={32} />
            <div className="leading-none">
              <p className="text-sm font-display font-bold text-gradient">TinySteps</p>
              <p className="text-[7px] text-gray-400 uppercase tracking-widest font-round">motherhood companion</p>
            </div>
          </Link>
        ) : (
          <button onClick={() => navigate(-1)}
            className="btn-icon -ml-1.5 w-9 h-9">
            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        )}

        {/* ── Center title ── */}
        {!isDash && title && (
          <p className="absolute left-1/2 -translate-x-1/2 text-sm font-semibold text-gray-800 dark:text-gray-100 font-round truncate max-w-[180px]">
            {title}
          </p>
        )}

        {/* ── Right actions ── */}
        <div className="ml-auto flex items-center gap-1">

          {/* ── 🗓 Test Date Picker ── */}
          <div className="relative" ref={pickerRef}>
            <button
              onClick={() => setShowPicker(v => !v)}
              title="Test Date Override"
              style={{
                display:'flex', alignItems:'center', gap:4, padding:'4px 9px', borderRadius:20,
                background: isMocked ? 'linear-gradient(135deg,#EA86AA,#AF83EC)' : 'rgba(246,141,179,0.12)',
                border: isMocked ? 'none' : '1.5px solid rgba(246,141,179,0.35)',
                cursor:'pointer', transition:'all 0.2s',
                boxShadow: isMocked ? '0 2px 8px rgba(234,134,170,0.35)' : 'none',
              }}>
              <Calendar style={{ width:12, height:12, color: isMocked ? 'white' : '#c4569a' }} />
              <span style={{ fontSize:9.5, fontWeight:700, color: isMocked ? 'white' : '#c4569a', whiteSpace:'nowrap' }}>
                {isMocked
                  ? `${MONTHS[mockDate.getMonth()]} ${mockDate.getDate()}, ${mockDate.getFullYear()}`
                  : 'Test'}
              </span>
            </button>

            {showPicker && (
              <div style={{
                position:'absolute', right:0, top:'calc(100% + 8px)', width:280,
                background:'rgba(255,252,255,0.99)', backdropFilter:'blur(24px)',
                borderRadius:20, border:'2px solid rgba(246,141,179,0.42)',
                boxShadow:'0 12px 40px rgba(184,138,248,0.22)', zIndex:9999, padding:'14px 16px',
              }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                  <p style={{ margin:0, fontSize:11.5, fontWeight:700, color:'#5e3e7e' }} className="font-round">📅 Test Date</p>
                  {isMocked && (
                    <button onClick={() => { clearMock(); setShowPicker(false); }}
                      style={{ fontSize:9.5, fontWeight:700, color:'#e04060', background:'rgba(220,38,38,0.08)', border:'1px solid rgba(220,38,38,0.20)', borderRadius:20, padding:'2px 8px', cursor:'pointer' }}
                      className="font-round">Reset to Today</button>
                  )}
                </div>

                {/* Month navigation */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                  <button onClick={() => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); }}
                    style={{ width:28, height:28, borderRadius:8, background:'rgba(246,141,179,0.12)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#c4569a' }}>
                    <ChevronLeft style={{ width:14, height:14 }} />
                  </button>
                  <span style={{ fontSize:12, fontWeight:700, color:'#3a2d48' }} className="font-round">
                    {MONTH_FULL[viewMonth]} {viewYear}
                  </span>
                  <button onClick={() => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); }}
                    style={{ width:28, height:28, borderRadius:8, background:'rgba(246,141,179,0.12)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#c4569a' }}>
                    <ChevronRight style={{ width:14, height:14 }} />
                  </button>
                </div>

                {/* Day-of-week labels */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:4 }}>
                  {['S','M','T','W','T','F','S'].map((d, i) => (
                    <p key={i} className="font-round" style={{ textAlign:'center', fontSize:9, fontWeight:700, color:'#a880c0', margin:0, padding:'2px 0' }}>{d}</p>
                  ))}
                </div>

                {/* Calendar grid */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
                  {calGrid.map((day, idx) => (
                    <button key={idx}
                      onClick={() => { if (day) { setMockDate(viewYear, viewMonth, day); setShowPicker(false); } }}
                      disabled={!day}
                      style={{
                        height:30, borderRadius:8, border:'none', cursor: day ? 'pointer' : 'default',
                        fontSize:11, fontWeight: isSelected(day) || isToday(day) ? 700 : 400,
                        background: isSelected(day)
                          ? 'linear-gradient(135deg,#EA86AA,#AF83EC)'
                          : isToday(day)
                          ? 'rgba(246,141,179,0.18)'
                          : 'transparent',
                        color: isSelected(day) ? 'white' : isToday(day) ? '#c4569a' : day ? '#3a2d48' : 'transparent',
                        transition:'background 0.15s',
                      }}
                      className="font-round">
                      {day || ''}
                    </button>
                  ))}
                </div>

                {isMocked && (
                  <p className="font-round" style={{ margin:'10px 0 0', fontSize:9.5, color:'#c4569a', textAlign:'center', fontStyle:'italic' }}>
                    ⚠ Test mode — app date set to {MONTH_FULL[selectedMonth]} {selectedDay}, {selectedYear}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Theme */}
          <button onClick={toggleTheme} className="btn-icon w-9 h-9">
            {theme === 'light'
              ? <Moon className="w-4 h-4 text-lavender-400" />
              : <Sun  className="w-4 h-4 text-amber-400" />}
          </button>

          {/* Notifications */}
          <div className="relative">
            <button onClick={() => { setNotif(!showNotif); setDropdown(false); }} className="btn-icon w-9 h-9 relative">
              <Bell className="w-4 h-4 text-blush-400" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-blush-500 rounded-full" />
            </button>
            {showNotif && (
              <div className="absolute right-0 mt-2 w-72 glass rounded-3xl shadow-float border border-blush-100 overflow-hidden animate-slideDown z-50">
                <div className="px-4 py-3 border-b border-blush-50">
                  <p className="text-sm font-semibold text-gray-700 font-round">Notifications</p>
                </div>
                {notifications.map(n => (
                  <div key={n.id} className="px-4 py-3 hover:bg-blush-50 transition-colors border-b border-blush-50/50 last:border-0">
                    <p className="text-xs text-gray-700 font-round">{n.text}</p>
                    <p className="text-[10px] text-gray-400 font-round mt-0.5">{n.time}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Avatar */}
          <div className="relative">
            <button onClick={() => { setDropdown(!showDropdown); setNotif(false); }}
              className="w-8 h-8 rounded-2xl bg-gradient-to-br from-blush-300 to-lavender-400 flex items-center justify-center text-white text-xs font-bold shadow-blush">
              {user?.name?.charAt(0)?.toUpperCase() || 'M'}
            </button>
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-44 glass rounded-3xl shadow-float border border-blush-100 overflow-hidden animate-slideDown z-50">
                <Link to="/profile" className="flex items-center gap-2.5 px-4 py-3 text-xs text-gray-700 hover:bg-blush-50 font-round" onClick={() => setDropdown(false)}>
                  <User className="w-3.5 h-3.5 text-blush-400" /> Profile
                </Link>
                <Link to="/profile" className="flex items-center gap-2.5 px-4 py-3 text-xs text-gray-700 hover:bg-blush-50 font-round" onClick={() => setDropdown(false)}>
                  <Settings className="w-3.5 h-3.5 text-blush-400" /> Settings
                </Link>
                <button onClick={() => { logout(); navigate('/'); }}
                  className="w-full flex items-center gap-2.5 px-4 py-3 text-xs text-coral-500 hover:bg-blush-50 font-round">
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
